import { createSlice } from '@reduxjs/toolkit';

/**
 * Cookie consent slice.
 *
 * Tracks whether the visitor has answered the cookie banner, and which
 * categories they've allowed.
 *
 * Categories:
 *   - essential : always true (auth, cart persistence — required for site to work)
 *   - analytics : optional (e.g. Google Analytics, Hotjar) — gated until accepted
 *   - marketing : optional (e.g. Meta Pixel, ad retargeting) — gated until accepted
 *
 * The banner uses `decision === null` to know it should be shown.
 * Once the user picks Accept All / Reject Non-Essential / Save Preferences,
 * `decision` becomes 'all' | 'essential' | 'custom' and the banner stays
 * dismissed across sessions (this slice is persisted).
 */

const initialState = {
  decision: null, // null | 'all' | 'essential' | 'custom'
  decidedAt: null, // ISO timestamp
  preferences: {
    essential: true, // immutable
    analytics: false,
    marketing: false,
  },
};

const cookieConsentSlice = createSlice({
  name: 'cookieConsent',
  initialState,
  reducers: {
    acceptAll(state) {
      state.decision = 'all';
      state.decidedAt = new Date().toISOString();
      state.preferences = { essential: true, analytics: true, marketing: true };
    },
    rejectNonEssential(state) {
      state.decision = 'essential';
      state.decidedAt = new Date().toISOString();
      state.preferences = { essential: true, analytics: false, marketing: false };
    },
    savePreferences(state, action) {
      state.decision = 'custom';
      state.decidedAt = new Date().toISOString();
      state.preferences = {
        essential: true,
        analytics: Boolean(action.payload?.analytics),
        marketing: Boolean(action.payload?.marketing),
      };
    },
    resetConsent() {
      return initialState;
    },
  },
});

export const {
  acceptAll: acceptAllCookies,
  rejectNonEssential: rejectNonEssentialCookies,
  savePreferences: saveCookiePreferences,
  resetConsent: resetCookieConsent,
} = cookieConsentSlice.actions;

export const selectCookieDecision    = (s) => s.cookieConsent.decision;
export const selectCookiePreferences = (s) => s.cookieConsent.preferences;
export const selectShouldShowBanner  = (s) => s.cookieConsent.decision === null;

export default cookieConsentSlice.reducer;
